/**
 * ConflictStep Component
 *
 * Displays conflict resolution UI when conflicts are detected.
 * Also shows absence summary when applicable.
 */

import React, { useEffect } from "react";
import classNames from "classnames";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Spinner } from "@/components/ui/feedback/Spinner";
import { ConflictResolver } from "@/components/booking/conflict/ConflictResolver";
import { formatAPIDate, formatDisplayDate } from "@/utils/date";
import { formatDecimal } from "@/utils/number/formatters";
import type { ConflictStepProps } from "../types";
import styles from "../AddForm.module.css";

export const ConflictStep: React.FC<ConflictStepProps> = ({
  conflictResolution,
  absenceSummary,
  onCancel,
  onResolve,
  className,
  "data-testid": testId,
}) => {
  const hasAbsenceAdjustments =
    absenceSummary.skippedDates.length > 0 ||
    absenceSummary.partialAdjustments.length > 0;

  // Auto-resolve if no conflicts, no absence data, and loading is complete
  useEffect(() => {
    if (
      !conflictResolution.isLoading &&
      !conflictResolution.hasConflicts &&
      !hasAbsenceAdjustments
    ) {
      // Small delay to allow UI to show briefly before auto-resolving
      const timer = setTimeout(() => {
        onResolve();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    conflictResolution.isLoading,
    conflictResolution.hasConflicts,
    hasAbsenceAdjustments,
    onResolve,
  ]);

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      <div className={styles.conflictStep} data-testid="add-form-conflict">
        {/* Absence summary */}
        {hasAbsenceAdjustments && (
          <div className={styles.absenceSummary} data-testid="absence-summary">
            <p>
              Noen av de valgte dagene har registrert fravær. Dager med fullt
              fravær hoppes over, og dager med delvis fravær fylles opp til en
              full dag.
            </p>
            <ul>
              {absenceSummary.skippedDates.map((date) => (
                <li key={formatAPIDate(date)}>
                  {formatDisplayDate(date)} – fullt fravær, ingen booking
                  opprettet.
                </li>
              ))}
              {absenceSummary.partialAdjustments.map((adj) => (
                <li key={formatAPIDate(adj.date)}>
                  {formatDisplayDate(adj.date)} – registrerer{" "}
                  {formatDecimal(adj.remainingHours)}t (fravær:{" "}
                  {formatDecimal(adj.absenceHours)}t).
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Loading state */}
        {conflictResolution.isLoading && (
          <div className={styles.loadingContainer}>
            <Spinner size="md" />
            <span className={styles.loadingText}>Sjekker konflikter...</span>
          </div>
        )}

        {/* Conflict resolver table */}
        {!conflictResolution.isLoading && conflictResolution.hasConflicts && (
          <div data-testid="add-form-has-conflicts">
            <ConflictResolver
              conflicts={conflictResolution.conflictInfos}
              omittedIndices={conflictResolution.omittedIndices}
              totalConflictDates={conflictResolution.totalConflictDates}
              onToggleOmit={conflictResolution.toggleOmit}
              onSaveAll={conflictResolution.saveAll}
              onOmitAll={conflictResolution.omitAll}
              formatHours={conflictResolution.formatHours}
              formatDate={conflictResolution.formatConflictDate}
              data-testid="conflict-resolver"
            />
            <div className={styles.actions}>
              <ActionButton
                label="Tilbake"
                icon="chevronLeft"
                variant="secondary"
                onClick={onCancel}
                data-testid="add-form-conflict-cancel"
              />
              <ActionButton
                label="Bekreft endringer"
                icon="save"
                onClick={onResolve}
                disabled={conflictResolution.allOmitted}
                data-testid="add-form-conflict-save"
              />
            </div>
          </div>
        )}

        {/* No conflicts - show simple save */}
        {!conflictResolution.isLoading && !conflictResolution.hasConflicts && (
          <div className={styles.actions}>
            <ActionButton
              label="Avbryt"
              icon="x"
              variant="secondary"
              onClick={onCancel}
              data-testid="add-form-conflict-cancel"
            />
            <ActionButton
              label="Lagre"
              icon="check"
              onClick={onResolve}
              data-testid="add-form-conflict-save"
            />
          </div>
        )}
      </div>
    </div>
  );
};

ConflictStep.displayName = "ConflictStep";

export default ConflictStep;
